import { flatten, head } from 'ramda'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { Col, Grid } from '../../../packages/kiti/components/structure/Grid'
import { useModalData } from '../../../components/modals/ModalsContext'
import { useOpenEditTriggerModal } from '../../../utils/routes/modals/modalsActions'
import { usePlaceTypes } from '../../../utils/helpers/placeTypes'
import Button from '../../../packages/kiti/components/actions/Button'
import Icon from '../../../packages/kiti/components/shapes/Icon'
import Panel from '../../../packages/kiti/components/structure/Panel'
import Separator from '../../../packages/kiti/components/helpers/Separator'
import Text from '../../../packages/kiti/components/presentation/Text'

const useOptions = () => {
  const { t } = useTranslation('triggers')
  const [placeTypes] = usePlaceTypes()

  return [
    {
      label: t('templates.onCompleteActivitySendEmail'),
      icon: 'RiMailSendLine',
      initialState: {
        entity: 'event',
        event: 'insert',
        action: 'email',
        conditions: [{ field: 'status_id', value: { id: 8, name: 'Concluído' } }]
      }
    },
    {
      label: t('templates.onCompleteActivityCallExternalApi'),
      icon: 'RiLink',
      initialState: {
        entity: 'event',
        event: 'insert',
        action: 'webhook',
        conditions: [{ field: 'status_id', value: { id: 8, name: 'Concluído' } }]
      }
    },
    {
      label: t('templates.onCompleteActivitySendSlackMessage'),
      icon: 'RiSlackFill',
      service: 'slack',
      initialState: {
        entity: 'event',
        event: 'insert',
        action: 'slack_channel_message',
        conditions: [{ field: 'status_id', value: { id: 8, name: 'Concluído' } }]
      }
    },
    {
      label: t('templates.onCompleteActivitySendDocumentToSign'),
      icon: 'RiArticleLine',
      service: 'd4sign',
      initialState: {
        entity: 'event',
        event: 'insert',
        action: 'd4sign_send_to_sing',
        conditions: [{ field: 'status_id', value: { id: 8, name: 'Concluído' } }]
      }
    },
    {
      label: t('templates.onCompleteActivityAttachDocument'),
      icon: 'RiArticleLine',
      service: 'd4sign',
      initialState: {
        entity: 'event',
        event: 'insert',
        action: 'd4sign_attach_document',
        payload: {
          origin: {
            id: 'service_order',
            name: t('actions.originActivity'),
            value: { id: '::{{event.service_order.id}}', checklist_id: '::{{event.service_order.checklist_id}}' }
          }
        },
        conditions: [{ field: 'status_id', value: { id: 8, name: 'Concluído' } }]
      }
    },
    {
      label: t('templates.onCompleteActivitySendWhatsappTwilio'),
      icon: 'RiArticleLine',
      service: 'twilio',
      initialState: {
        entity: 'event',
        event: 'insert',
        action: 'whatsapp_by_twilio',
        conditions: [{ field: 'status_id', value: { id: 8, name: 'Concluído' } }]
      }
    },
    {
      label: t('templates.onCompleteActivitySendSmsTwilio'),
      icon: 'RiArticleLine',
      service: 'twilio',
      initialState: {
        entity: 'event',
        event: 'insert',
        action: 'sms_by_twilio',
        conditions: [{ field: 'status_id', value: { id: 8, name: 'Concluído' } }]
      }
    },
    {
      label: t('templates.onCompleteActivitySendWhatsappChatPro'),
      icon: 'RiArticleLine',
      service: 'chat_pro',
      initialState: {
        entity: 'event',
        event: 'insert',
        action: 'whatsapp_by_chat_pro',
        conditions: [{ field: 'status_id', value: { id: 8, name: 'Concluído' } }]
      }
    },

    {
      label: t('templates.onCreateActivityCreateGoogleCalendarEvent'),
      icon: 'RiCalendarCheckFill',
      service: 'google_calendar',
      initialState: {
        entity: 'event',
        event: 'insert',
        action: 'google_calendar_insert_event',
        conditions: [{ field: 'status_id', value: { id: 1, name: 'Aberta' } }]
      }
    },
    {
      label: t('templates.onCompleteActivityCreateNewActivity'),
      icon: 'RiInboxLine',
      initialState: {
        entity: 'event',
        event: 'insert',
        action: 'insert_service_order',
        conditions: [{ field: 'status_id', value: { id: 8, name: 'Concluído' } }]
      }
    },
    {
      label: t('templates.onCreateActivityCreateAnotherActivity'),
      icon: 'RiInboxLine',
      initialState: { entity: 'service_order', event: 'insert', action: 'insert_service_order' }
    },

    {
      label: t('templates.onCreateUserSendEmail'),
      icon: 'RiMailSendLine',
      initialState: { entity: 'user', event: 'insert', action: 'email' }
    },
    {
      label: t('templates.onCreateUserCallExternalApi'),
      icon: 'RiLink',
      initialState: { entity: 'user', event: 'insert', action: 'webhook' }
    },
    {
      label: t('templates.onCreateUserCreateActivity'),
      icon: 'RiInboxLine',
      initialState: { entity: 'user', event: 'insert', action: 'insert_service_order' }
    },

    {
      label: t('templates.onReplyFormSendEmail'),
      icon: 'BsEnvelope',
      initialState: { entity: 'form_reply', event: 'insert', action: 'email' },
      conditions: [{ field: 'form_id', value: {} }]
    },
    {
      label: t('templates.onReplyFormCallExternalApi'),
      icon: 'IoMdPaperPlane',
      initialState: { entity: 'form_reply', event: 'insert', action: 'webhook' },
      conditions: [{ field: 'form_id', value: {} }]
    },
    {
      label: t('templates.onReplyFormCreateActivity'),
      icon: 'IoIosFiling',
      initialState: { entity: 'form_reply', event: 'insert', action: 'insert_service_order' },
      conditions: [{ field: 'form_id', value: {} }]
    },

    {
      label: t('templates.onCreateNonComplianceInstanceSendEmail'),
      icon: 'RiMailSendLine',
      initialState: {
        entity: 'non_compliance_event',
        event: 'insert',
        action: 'email',
        conditions: [{ field: 'non_compliance_event.type', value: { id: 'instance', name: 'Ocorrência' } }]
      }
    },
    {
      label: t('templates.onCreateNonComplianceInstanceCallExternalApi'),
      icon: 'RiLink',
      initialState: {
        entity: 'non_compliance_event',
        event: 'insert',
        action: 'webhook',
        conditions: [{ field: 'non_compliance_event.type', value: { id: 'instance', name: 'Ocorrência' } }]
      }
    },
    {
      label: t('templates.onCreateNonComplianceInstanceCreateActivity'),
      icon: 'RiInboxLine',
      initialState: {
        entity: 'non_compliance_event',
        event: 'insert',
        action: 'insert_service_order',

        conditions: [{ field: 'non_compliance_event.type', value: { id: 'instance', name: 'Ocorrência' } }]
      }
    },

    {
      label: t('templates.onCreateFormsNonComplianceInstanceSendEmail'),
      icon: 'RiMailSendLine',
      initialState: {
        entity: 'forms_non_compliance_event',
        event: 'insert',
        action: 'email',
        conditions: [{ field: 'non_compliance_event.type', value: { id: 'instance', name: 'Ocorrência' } }]
      }
    },
    {
      label: t('templates.onCreateFormsNonComplianceInstanceCallExternalApi'),
      icon: 'RiLink',
      initialState: {
        entity: 'forms_non_compliance_event',
        event: 'insert',
        action: 'webhook',
        conditions: [{ field: 'non_compliance_event.type', value: { id: 'instance', name: 'Ocorrência' } }]
      }
    },
    {
      label: t('templates.onCreateFormsNonComplianceInstanceCreateActivity'),
      icon: 'RiInboxLine',
      initialState: {
        entity: 'forms_non_compliance_event',
        event: 'insert',
        action: 'insert_service_order',

        conditions: [{ field: 'non_compliance_event.type', value: { id: 'instance', name: 'Ocorrência' } }]
      }
    },

    ...flatten(
      placeTypes.map(type => [
        {
          label: t('templates.onCreatePlaceTypeSendEmail', { placeType: type.name }),
          icon: 'RiMailSendLine',
          initialState: { entity: type.key, event: 'insert', action: 'email' }
        },
        {
          label: t('templates.onCreatePlaceTypeCallExternalApi', { placeType: type.name }),
          icon: 'RiLink',
          initialState: { entity: type.key, event: 'insert', action: 'webhook' }
        },
        {
          label: t('templates.onCreatePlaceTypeCreateActivity', { placeType: type.name }),
          icon: 'RiInboxLine',
          initialState: { entity: type.key, event: 'insert', action: 'insert_service_order' }
        }
      ])
    )
  ]
}

const Card = ({ width, ...props }) => (
  <Col width={width || '33%'}>
    <Panel borderColor="light4" margin={0.5} minHeight={14} direction="column" {...props} />
  </Col>
)

const TemplatesList = () => {
  const { t } = useTranslation('triggers')
  const { entities, integrationId, service } = useModalData() || {}

  const openEditModal = useOpenEditTriggerModal()
  const filteredOptions = useOptions().filter(
    option => entities.includes(option.initialState?.entity) && option.service === service
  )

  return (
    <Grid wrap>
      <Card
        width="66%"
        borderColor="primary"
        borderStyle="dashed"
        onClick={() =>
          openEditModal({ entities, initialState: { integrationId, entity: head(entities), event: 'insert' } })
        }
      >
        <Icon name="RiPlayListAddFill" size={1.6} />
        <Separator weight={2} />
        <Text size="h3" bold>
          {t('templates.createCustomAutomation')}
        </Text>
        <Col flex={1} />
        <Button color="primary">{t('templates.createNew')}</Button>
      </Card>

      {filteredOptions.map(option => (
        <Card
          key={option.label}
          onClick={() => openEditModal({ entities, initialState: { integrationId, service, ...option.initialState } })}
        >
          <Icon name={option.icon} size={1.2} color="dark4" />
          <Separator weight={2} />
          <Text size="h4">{option.label}</Text>
          <Col flex={1} />
          <Button transparent={!option.highlight} color={option.highlight ? 'primary' : 'dark3'}>
            {t('templates.useTemplate')}
          </Button>
        </Card>
      ))}
    </Grid>
  )
}

export default TemplatesList
